"use client";

import { CopyButton, Field } from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H3 } from "@/components/tool-kit/styles";

/** The textarea class the kit uses for prose, with room for a non-Latin script. */
const SCRIPT_TEXTAREA =
  "min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-lg leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary";

/**
 * A labelled textarea that declares the language and writing direction of what is typed
 * into it, so a browser picks the right font and a right-to-left script is laid out
 * right to left. The kit's `TextAreaField` sets neither.
 */
export function ScriptTextArea({
  id,
  label,
  hint,
  value,
  onChange,
  lang,
  dir = "auto",
  rows = 4,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  /** BCP 47 tag of the text typed here, such as `ar` or `ka`. */
  lang?: string;
  dir?: "ltr" | "rtl" | "auto";
  rows?: number;
}) {
  return (
    <Field hint={hint} id={id} label={label}>
      <textarea
        className={SCRIPT_TEXTAREA}
        dir={dir}
        id={id}
        lang={lang}
        onChange={(event) => onChange(event.target.value)}
        rows={rows}
        spellCheck={false}
        value={value}
      />
    </Field>
  );
}

/** One system's output, with the language tag and direction of the text it shows. */
export function ResultCard({
  title,
  use,
  text,
  lang,
  dir = "ltr",
  copyLabel,
  copiedLabel,
}: {
  title: string;
  use?: string;
  text: string;
  lang?: string;
  dir?: "ltr" | "rtl" | "auto";
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{title}</h3>
      {use ? <p className={HINT}>{use}</p> : null}
      <p
        className="whitespace-pre-wrap break-words text-foreground text-lg leading-relaxed"
        dir={dir}
        lang={lang}
      >
        {text}
      </p>
      <CopyButton
        copiedLabel={copiedLabel}
        label={copyLabel}
        small
        text={text}
      />
    </div>
  );
}
